import React from "react";
import StatCard from "../StatCard";
import type { DashboardKPIs } from "../../hooks/useOperationsAnalytics";
import type { MetricSample } from "../../hooks/useMetricSamples";
import { formatUSD } from "@/utils/formatters";

// =============================================================================
// KPI GRID
// =============================================================================
// PURPOSE: Compact metric grid with color-coded StatCards showing 40+ KPIs across 4 rows.
//
// REQUIREMENTS:
// - Row 1: Core sales metrics (AOV, CPC, deals, close rate, show rate, slippage)
// - Row 2: Booking metrics (calls booked, booking rate, avg days to show)
// - Row 3: Call performance (calls taken, daily dials, connect rate)
// - Row 4: Funnel metrics (webinar signups, conversions, webinar→call, call→show, show→close)
// - Color-coded cards (green=revenue, orange=rates, blue=volume, darkblue=funnel)
// - Percent change indicators for all metrics
// =============================================================================

interface KPIGridProps {
  kpis: DashboardKPIs;
  getSamples?: (key: string) => MetricSample[];
}

const KPIGrid: React.FC<KPIGridProps> = ({ kpis, getSamples }) => {
  return (
    <div className="space-y-4">
      {/* Row 1: Core Sales Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 items-stretch">
        <StatCard
          title="AVG Order Value"
          value={formatUSD(kpis.aov)}
          color="green"
          compact
          percentChange={kpis.aovChange}
          changeFormat="dollar"
          metricKey="aov"
          getSamples={getSamples}
        />
        <StatCard
          title="Cash per Call Taken"
          value={formatUSD(kpis.cashPerCallTaken)}
          color="green"
          compact
          percentChange={kpis.cashPerCallTakenChange}
          changeFormat="dollar"
          metricKey="cashPerCallTaken"
          getSamples={getSamples}
        />
        <StatCard
          title="Cash per Call Booked"
          value={formatUSD(kpis.cashPerCallBooked)}
          color="green"
          compact
          percentChange={kpis.cashPerCallBookedChange}
          changeFormat="dollar"
          metricKey="cashPerCallBooked"
          getSamples={getSamples}
        />
        <StatCard
          title="Deals Closed"
          value={kpis.dealsClosed.toString()}
          color="darkblue"
          compact
          percentChange={kpis.dealsChange}
          changeFormat="number"
          metricKey="dealsClosed"
          getSamples={getSamples}
        />
        <StatCard
          title="Close Rate"
          value={`${kpis.closeRate.toFixed(1)}%`}
          color="darkblue"
          compact
          percentChange={kpis.closeRateChange}
          metricKey="closeRate"
          getSamples={getSamples}
        />
        {/* Quality of Prospects removed — non-inferable label (was DM→Book conversion rate) */}
      </div>

      {/* Row 2: Setting / DM Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 items-stretch">
        <StatCard
          title="Setter Booked Calls"
          value={kpis.setterBookedCalls.toString()}
          color="gray"
          compact
          metricKey="setterBookedCalls"
          getSamples={getSamples}
        />
        <StatCard
          title="Calls Booked by Closer"
          value={kpis.closerBookedCalls.toString()}
          color="gray"
          compact
          metricKey="closerBookedCalls"
          getSamples={getSamples}
        />
        <StatCard
          title="Show Up Rate"
          value={`${kpis.showUpRate.toFixed(1)}%`}
          color="gray"
          compact
          percentChange={kpis.showUpRateChange}
          metricKey="showUpRate"
          getSamples={getSamples}
        />
        <StatCard
          title="New Chats"
          value={kpis.newDMChats > 0 ? kpis.newDMChats.toString() : "--"}
          color="blue"
          compact
          subtext={kpis.newDMChats === 0 ? "Awaiting data" : undefined}
          metricKey="newDMChats"
          getSamples={getSamples}
        />
        <StatCard
          title="Conversations"
          value={kpis.deepConversations > 0 ? kpis.deepConversations.toString() : "--"}
          color="blue"
          compact
          subtext={kpis.deepConversations === 0 ? "Awaiting data" : undefined}
          metricKey="conversations"
          getSamples={getSamples}
        />
        <StatCard
          title="New Chats per Day"
          value={kpis.newDMsPerDay > 0 ? kpis.newDMsPerDay.toFixed(1) : "--"}
          color="blue"
          compact
          metricKey="newDMsPerDay"
          getSamples={getSamples}
        />
        <StatCard
          title="DM Convos per Day"
          value={kpis.dmConvosPerDay > 0 ? kpis.dmConvosPerDay.toFixed(1) : "--"}
          color="blue"
          compact
          metricKey="dmConvosPerDay"
          getSamples={getSamples}
        />
      </div>

      {/* Row 3: Dialing / Funnel Metrics */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 items-stretch">
        {/* Funnel Effectiveness removed — non-inferable label (was book-to-close rate) */}
        <StatCard
          title="Shows"
          value={kpis.shows.toString()}
          color="gray"
          compact
          metricKey="shows"
          getSamples={getSamples}
        />
        <StatCard
          title="Dials"
          value={kpis.dials > 0 ? kpis.dials.toString() : "--"}
          color="darkblue"
          compact
          subtext={kpis.dials === 0 ? "No dial data" : undefined}
          metricKey="dials"
          getSamples={getSamples}
        />
        <StatCard
          title="Dials per Day"
          value={kpis.dialsPerDay > 0 ? kpis.dialsPerDay.toString() : "--"}
          color="darkblue"
          compact
          metricKey="dialsPerDay"
          getSamples={getSamples}
        />
        <StatCard
          title="Dials per Convo"
          value={kpis.dialsPerConvo > 0 ? kpis.dialsPerConvo.toString() : "--"}
          color="darkblue"
          compact
          metricKey="dialsPerConvo"
          getSamples={getSamples}
        />
        <StatCard
          title="Dials Convo per Call"
          value={kpis.dialsConvoPerCall > 0 ? kpis.dialsConvoPerCall.toString() : "--"}
          color="darkblue"
          compact
          metricKey="dialsConvoPerCall"
          getSamples={getSamples}
        />
      </div>
    </div>
  );
};

export default KPIGrid;
